import React, { useCallback, useEffect, useState } from 'react';
import { taskService } from '../services/taskService';
import { useAuth } from '../context/AuthContext';
import Loading from '../components/common/Loading';
import TableSkeleton from '../components/common/TableSkeleton';

const InactiveTasksPage = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadInactiveTasks = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError('');
      const response = await taskService.getTasks({
        assignedTo: user.id,
        assignmentState: 'inactive'
      });

      const inactiveTasks = (response.data || [])
        .filter((task) => task.assignee?.id === user.id || (task.subAssignees || []).some((member) => member.id === user.id))
        .sort((a, b) => (a.title || '').localeCompare(b.title || ''));

      setTasks(inactiveTasks);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load inactive tasks.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadInactiveTasks();
  }, [loadInactiveTasks]);

  if (loading) {
    return (
      <div>
        <Loading />
        <div className="mt-4">
          <TableSkeleton rows={6} columns={7} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <h1 className="text-2xl font-bold">Inactive Tasks</h1>
        {tasks.length > 0 && (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">
            {tasks.length}
          </span>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      )}

      {tasks.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No inactive tasks</h3>
          <p className="text-gray-600">You do not have inactive assigned tasks right now.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target / Revised</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assignment State</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tasks.map((task) => (
                <tr key={task.id}>
                  <td className="px-6 py-4 whitespace-normal break-words text-primary">{task.title}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{task.client?.name || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{task.priority || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{task.status || 'Not Started'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="leading-tight">
                      <div>{task.targetDate || '-'}</div>
                      {task.revisedDate && <div className="text-xs text-indigo-700">Revised: {task.revisedDate}</div>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs rounded font-medium bg-gray-100 text-gray-700">
                      Inactive
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InactiveTasksPage;
